import { Http } from '@mygpt/app/http';
import { getLarkIntegrations, IntegrationRule, GetLarkIntegrationsParams } from '@mygpt/app/apis/services/lark';
import { ListResponse, ListPageParams, DatasetsFilesList } from '@mygpt/app/datasets/services';

// Web Connector 相关类型
export type RefreshFrequency = 'weekly' | 'monthly';

// V2 Schedule 配置
export interface ScheduleConfig {
  frequency: 'daily' | 'weekly' | 'monthly';
  day_of_week?: number; // 0-6 (0=Sun), weekly only
  day_of_month?: number; // 1-31, monthly only
  hour: number; // 0-23
  minute: number; // 0-59
  timezone: string; // e.g. "Asia/Tokyo"
}

// V2 Web Connector 配置
export interface WebConnectorV2Config {
  url_patterns: string[];
  exclude_patterns: string[];
  max_pages: number;
  is_spa: boolean;
  discovery_mode?: 'auto' | 'sitemap' | 'crawler';
  /**
   * 是否把精确 URL（不含通配符的 url_patterns 项）也作为 crawler 的额外起点。
   * 默认 false，保持既有行为（精确 URL 只作单页文档入库）。
   * 适用：hub 页带 query string 的站点（如 list.php?category=all），
   * 否则 `**` pattern 因 query 被丢弃而无法发现子页。
   */
  crawl_from_exact_urls?: boolean;
}

// Web Connector 相关接口类型
export interface WebConnectorList extends ListResponse {
  items: IntegrationRule[];
}

// V1 创建参数（保留兼容）
export interface CreateWebConnectorParams {
  type: string;
  name?: string;
  config: {
    enable_sync: boolean;
    interval_seconds: number;
    notification_emails?: string[];
    web_connector:
      | {
          // 递归爬取模式（学习整个网站）
          crawl_mode: 'recursive';
          base_url: string;
          max_pages: number;
          is_spa: boolean;
        }
      | {
          // 列表爬取模式（学习单个网页）
          crawl_mode: 'list';
          url_list: string[];
          is_spa: boolean;
        };
  };
}

// V2 创建参数
export interface CreateWebConnectorV2Params {
  type: 'web_connector';
  name: string;
  config: {
    enable_sync: boolean;
    schedule?: ScheduleConfig;
    notification_emails?: string[];
    web_connector: WebConnectorV2Config;
  };
}

// V2 更新参数
export interface UpdateWebConnectorV2Params {
  name: string;
  config: {
    enable_sync: boolean;
    schedule?: ScheduleConfig;
    notification_emails?: string[];
    web_connector: WebConnectorV2Config;
  };
}

// V1 更新参数（保留兼容）
export interface UpdateWebConnectorParams {
  name: string;
  config: {
    enable_sync: boolean;
    interval_seconds: number;
    notification_emails?: string[];
    web_connector: {
      base_url?: string;
      url_list?: string[];
      max_pages?: number;
      crawl_mode: 'recursive' | 'list';
      is_spa: boolean;
    };
  };
}

// URL Pattern 预览
export interface PatternPreviewItem {
  pattern: string;
  is_exact: boolean;
  matched_count: number;
  sample_urls: string[];
}

export interface PreviewResponse {
  total_urls: number;
  excluded_count: number;
  patterns: PatternPreviewItem[];
}

// Web Connector 相关 API
/**
 * 获取 Web Connector 列表
 * @param datasetId
 * @param params 分页和排序参数
 * @returns
 */
export const getWebConnectors = (datasetId: string, params?: Omit<GetLarkIntegrationsParams, 'type'>) => {
  // 使用 getLarkIntegrations 接口，type 为 web_connector
  return getLarkIntegrations(datasetId, {
    type: ['web_connector'],
    ...params,
  });
};

/**
 * 创建 Web Connector (V1)
 * @param params
 * @returns
 */
export const createWebConnector = (datasetId: string, params: CreateWebConnectorParams) => {
  const { post } = Http.use();
  return post<IntegrationRule>(`/datasets/${datasetId}/integrations`, params);
};

/**
 * 创建 Web Connector (V2 - URL patterns)
 */
export const createWebConnectorV2 = (datasetId: string, params: CreateWebConnectorV2Params) => {
  const { post } = Http.use();
  return post<IntegrationRule>(`/datasets/${datasetId}/integrations`, params);
};

/**
 * 更新 Web Connector
 * @param datasetId
 * @param connectorId
 * @param params
 * @returns
 */
export const updateWebConnector = (datasetId: string, connectorId: string, params: UpdateWebConnectorParams) => {
  const { put } = Http.use();
  return put<IntegrationRule>(`/datasets/${datasetId}/integrations/${connectorId}`, params);
};

/**
 * 更新 Web Connector (V2)
 */
export const updateWebConnectorV2 = (datasetId: string, connectorId: string, params: UpdateWebConnectorV2Params) => {
  const { put } = Http.use();
  return put<IntegrationRule>(`/datasets/${datasetId}/integrations/${connectorId}`, params);
};

/**
 * 删除 Web Connector
 * @param datasetId
 * @param connectorId
 * @returns
 */
export const deleteWebConnector = (datasetId: string, connectorId: string) => {
  const { delete: del } = Http.use();
  return del(`/datasets/${datasetId}/integrations/${connectorId}`);
};

/**
 * 手动触发 Web Connector 更新
 * @param datasetId
 * @param connectorId
 * @returns
 */
export const refreshWebConnector = (datasetId: string, connectorId: string) => {
  const { post } = Http.use();
  return post(`/datasets/${datasetId}/integrations/${connectorId}/sync`);
};

/**
 * 获取 Web Connector 的文件列表（二级列表）
 * @param datasetId
 * @param integrationRuleId
 * @param params
 * @returns
 */
export const getWebConnectorFiles = (datasetId: string, integrationRuleId: string, params?: ListPageParams) => {
  const { get } = Http.use();
  return get<DatasetsFilesList>(`/datasets/${datasetId}/files`, {
    params: {
      file_type: 'integration',
      integration_rule_id: integrationRuleId,
      ...params,
    },
  });
};

/**
 * 预览 URL patterns 匹配结果 (V2)
 */
export const previewUrlPatterns = (
  datasetId: string,
  params: {
    url_patterns: string[];
    exclude_patterns: string[];
    max_pages: number;
    is_spa?: boolean;
    discovery_mode?: 'auto' | 'sitemap' | 'crawler';
    crawl_from_exact_urls?: boolean;
  }
) => {
  const { post } = Http.use();
  return post<PreviewResponse>(`/datasets/${datasetId}/integrations/preview`, params);
};
